import type { Period } from '../hooks/usePeriodParams';

/**
 * revenueRecognition route paths, declared once so App routing and in-app navigation share one
 * source of truth (mirrors `modules/claims/routes/routeConfig.ts`).
 */
export const REVREC_HOME_PATH = '/revrec';
export const REVREC_CENTER_PATH = '/revrec/centers/:center';

/**
 * Both new screens are about one center's month, so both sit under the center path — and Go Back on
 * the detail rebuilds the summary URL, period included, from what the detail already holds.
 */
export const REVREC_PERIOD_SUMMARY_PATH = `${REVREC_CENTER_PATH}/summary`;
export const REVREC_ACCOUNT_DETAIL_PATH = `${REVREC_CENTER_PATH}/accounts/:accountId`;
/** The out-of-period adjustments for one center's month — also a center-month screen, so also under
 * the center path, and the period rides in the search parameters like every other link here. */
export const REVREC_OUT_OF_PERIOD_PATH = `${REVREC_CENTER_PATH}/out-of-period`;

/**
 * The upload wizard has three doors: the module home opens it across every ACTIVE COMPANY center —
 * not every center the user can reach, because nothing on this screen consults the profile's
 * `centerAccess` — and the center dashboard and the out-of-period screen each open it with their
 * own center fixed.
 * Two paths rather than one, because the two differ by a whole `centers/:center` prefix and no
 * single pattern spans that: an optional segment degrades to `/revrec/centers/adjustment-upload`,
 * never to the bare path. The center-fixed door therefore sits under the center path beside every
 * other center-month screen, and the page tells the doors apart by whether `:center` resolved.
 */
export const REVREC_ADJUSTMENT_UPLOAD_PATH = '/revrec/adjustment-upload';
export const REVREC_CENTER_ADJUSTMENT_UPLOAD_PATH = `${REVREC_CENTER_PATH}/adjustment-upload`;

/**
 * A center dashboard link that carries the period the user is looking at — the URL is the only copy
 * of the period, so a link that dropped it would land the next screen on the current month.
 */
export const centerDashboardPath = (center: string, period: Period): string =>
  `${REVREC_CENTER_PATH.replace(':center', center)}?year=${period.year}&month=${period.month}`;

/**
 * The same period-carrying links for the two new screens. `accountId` is encoded because a document
 * id is not guaranteed path-safe; the center is not, matching `centerDashboardPath`.
 */
export const periodSummaryPath = (center: string, period: Period): string =>
  `${REVREC_PERIOD_SUMMARY_PATH.replace(':center', center)}?year=${period.year}&month=${period.month}`;

export const outOfPeriodPath = (center: string, period: Period): string =>
  `${REVREC_OUT_OF_PERIOD_PATH.replace(':center', center)}?year=${period.year}&month=${period.month}`;

/**
 * Pending approvals is all-centers, not one center's month, so it sits at the top level rather than
 * under `REVREC_CENTER_PATH` — `usePendingApprovals` reads `center` from the query string, not a path
 * parameter. `period` is required because the query always carries one; `center` is optional because
 * the page's own default is All centers, which this builder represents by omitting the parameter.
 */
export const REVREC_PENDING_APPROVALS_PATH = '/revrec/pending-approvals';

export const pendingApprovalsPath = (period: Period, center?: string): string =>
  `${REVREC_PENDING_APPROVALS_PATH}?year=${period.year}&month=${period.month}${center ? `&center=${center}` : ''}`;

/** Period close carries no period in its URL — the page's own table is the month selector. */
export const REVREC_PERIOD_CLOSE_PATH = '/revrec/period-close';

/**
 * Two builders for the three doors: the two center-fixed doors share one, because they differ only
 * in which center they pass. The all-centers door takes no center because the file's own Center
 * column picks one per row. Both carry the period, which the wizard seeds its selector from.
 */
export const adjustmentUploadPath = (period: Period): string =>
  `${REVREC_ADJUSTMENT_UPLOAD_PATH}?year=${period.year}&month=${period.month}`;

export const centerAdjustmentUploadPath = (center: string, period: Period): string =>
  `${REVREC_CENTER_ADJUSTMENT_UPLOAD_PATH.replace(':center', center)}?year=${period.year}&month=${period.month}`;

/**
 * `tab` is optional and names the detail tab to open — the out-of-period grid sends a row straight to
 * that account's Out of period tab. One optional parameter on this builder rather than a second
 * builder: the destination is the same page, and a caller that omits it gets the page's first tab,
 * which is what every existing call site already relies on.
 */
export const accountDetailPath = (
  center: string,
  accountId: string,
  period: Period,
  tab?: string,
): string =>
  `${REVREC_ACCOUNT_DETAIL_PATH.replace(':center', center).replace(
    ':accountId',
    encodeURIComponent(accountId),
  )}?year=${period.year}&month=${period.month}${tab ? `&tab=${tab}` : ''}`;
